import { concatAll } from "../concat-all";
import { isErrorWithMessage, isErrorWithNestedMessage } from "./error-refinements";
import { isWalletError } from "./WalletError";

export type ExtractErrorMessage = (error: unknown) => string;
/**
 * Nullable version of `ExtractErrorMessage`. Null value means that
 * an error message could not be extracted for a given error
 */
export type TryExtractingErrorMessage = (error: unknown) => string | undefined;

/**
 * Converts `TryExtractingErrorMessage` to `ExtractErrorMessage`
 */
export const toExtractErrorMessage =
  (onFallback: ExtractErrorMessage) =>
  (_try: TryExtractingErrorMessage): ExtractErrorMessage =>
  (error) =>
    _try(error) ?? onFallback(error);

/**
 * Will try the first given function to extract an error message - if that
 * doesn't return a value - it will try the second given function
 */
export const concatTryExtractingErrorMessage =
  (a: TryExtractingErrorMessage, b: TryExtractingErrorMessage): TryExtractingErrorMessage =>
  (error) =>
    a(error) ?? b(error);

/**
 * Will use the first of the given functions that returns a non nullable value
 */
export const concatAllTryExtractingErrorMessage = concatAll<TryExtractingErrorMessage>(
  concatTryExtractingErrorMessage,
  () => undefined
);

const errorInstance: TryExtractingErrorMessage = (error) => (error instanceof Error ? error.message : undefined);

const walletError: TryExtractingErrorMessage = (error) => (isWalletError(error) ? error.message : undefined);

const nestedMessage: TryExtractingErrorMessage = (error) =>
  isErrorWithNestedMessage(error) ? error.data.message : undefined;

const withMessage: TryExtractingErrorMessage = (error) => (isErrorWithMessage(error) ? error.message : undefined);

export const defaultTryExtractErrorMessage = concatAllTryExtractingErrorMessage([
  errorInstance,
  walletError,
  nestedMessage,
  withMessage,
]);

export const defaultExtractErrorMessage = toExtractErrorMessage(() => "Unknown error occurred")(
  defaultTryExtractErrorMessage
);
